import { ValidationError, number, object, ref } from 'yup';
import {
    AlarmSettingValidationField,
    alarmSettingValidator,
} from '../alarm-settings/alarm-setting-validator';
import { Channel, ChannelFns } from './../../models/channel';
import { GenericErrorStrings } from './../../validators/validation-errors/generic';
import { ChannelFcuData } from './../../models/channel-fcu-data';

function createAlarmSettingValidator(setting: AlarmSettingValidationField) {
    return function (value) {
        let channel = this.from[this.from.length - 1].value as Channel;
        let validator = alarmSettingValidator(channel, setting);
        validator.validateSync(value, this.options);
        return true;
    };
}

function lessThanMeasurableMax(value) {
    let fcuData = this.from[1].value as ChannelFcuData;
    return value > fcuData.measurableRange.max
        ? this.createError({
              message: GenericErrorStrings.ABOVE_MAXIMUM,
              params: { max: fcuData.measurableRange.max },
          })
        : true;
}

function greaterThanMeasurableMin(value) {
    let fcuData = this.from[1].value as ChannelFcuData;
    return value < fcuData.measurableRange.min
        ? this.createError({
              message: GenericErrorStrings.BELOW_MINIMUM,
              params: { min: fcuData.measurableRange.min },
          })
        : true;
}

let digitalAlarmValidator = createAlarmSettingValidator(AlarmSettingValidationField.ALARM_SETTING);
let veryLowValidator = createAlarmSettingValidator(AlarmSettingValidationField.ALARM_VERY_LOW);
let lowValidator = createAlarmSettingValidator(AlarmSettingValidationField.ALARM_LOW);
let highValidator = createAlarmSettingValidator(AlarmSettingValidationField.ALARM_HIGH);
let veryHighValidator = createAlarmSettingValidator(AlarmSettingValidationField.ALARM_VERY_HIGH);
let feedbackErrorValidator = createAlarmSettingValidator(
    AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR,
);
let sensorFailValidator = createAlarmSettingValidator(
    AlarmSettingValidationField.ALARM_SETTING_SENSOR_FAIL,
);

export function channelValidator() {
    return object({
        fcuData: object({
            scale: number().strict().integer(),
            measurableRange: object({
                min: number().strict().integer(),
                max: number().strict().integer().moreThan(ref('min')),
            }).required(),
            normalRange: object({
                min: number()
                    .strict()
                    .integer()
                    .test(lessThanMeasurableMax)
                    .test(greaterThanMeasurableMin),
                max: number()
                    .strict()
                    .integer()
                    .moreThan(ref('min'))
                    .test(lessThanMeasurableMax)
                    .test(greaterThanMeasurableMin),
            }).required(),
            alarmSetting: object().required().test(digitalAlarmValidator),
            alarmSettingVeryLow: object().required().test(veryLowValidator),
            alarmSettingLow: object().required().test(lowValidator),
            alarmSettingHigh: object().required().test(highValidator),
            alarmSettingVeryHigh: object().required().test(veryHighValidator),
            alarmSettingFeedbackError: object().required().test(feedbackErrorValidator),
            alarmSettingSensorFail: object().required().test(sensorFailValidator),
        }),
    });
}

export function scaleErrorValues(channel: Channel, error: ValidationError) {
    if (!error.inner.length) scaleError(channel, error);

    for (let i = 0; i < error.inner.length; i++) {
        scaleError(channel, error.inner[i]);
    }
}

function scaleError(channel: Channel, error: ValidationError) {
    if (typeof error.params.value === 'number') {
        error.params.value = ChannelFns.scaleValue(channel, error.params.value as number);
    }

    if (error.params.less != null) {
        error.params.less = ChannelFns.scaleValue(channel, error.params.less as number);
    }

    if (error.params.more != null) {
        error.params.more = ChannelFns.scaleValue(channel, error.params.more as number);
    }

    if (error.params.min != null) {
        error.params.min = ChannelFns.scaleValue(channel, error.params.min as number);
    }

    if (error.params.max != null) {
        error.params.max = ChannelFns.scaleValue(channel, error.params.max as number);
    }
}
